import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi, type AuthService } from '@/api/auth';
import { AUTH_LOGOUT_EVENT } from '@/api/client';
import { AuthContext, type AuthContextValue } from './authContextDef';
import type { LoginRequest, User } from '@/types/auth';

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode;
  /**
   * Auth service (injectable for tests)
   * @default authApi
   */
  service?: AuthService;
}

/**
 * Auth provider component that manages authentication state
 */
export function AuthProvider({ children, service = authApi }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  /**
   * Restore the session from stored tokens on mount
   */
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        if (!service.hasStoredSession()) {
          return;
        }
        if (!service.hasValidAccessToken()) {
          // Tokens exist but the access token expired - refresh first
          await service.refresh();
        }
        const currentUser = await service.getCurrentUser();
        if (!cancelled) setUser(currentUser);
      } catch {
        // Session could not be restored
        service.clearAuth();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [service]);

  /**
   * Listen for forced logout events from the API client
   */
  useEffect(() => {
    const handleForcedLogout = () => setUser(null);

    window.addEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleForcedLogout);
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const response = await service.login(credentials);
      setUser(response.user);
    },
    [service]
  );

  const logout = useCallback(async () => {
    try {
      await service.logout();
    } finally {
      setUser(null);
    }
  }, [service]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isInitializing,
      login,
      logout,
    }),
    [user, isInitializing, login, logout]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
